import { hasAskInputQuery } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hasAskInputQuery.js';
import { useAskInputKeydown } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputKeydown.js';
import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { getQueryWithoutSourceMentions } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { getSubmissionPolicy } from '#pplx/frontend/apps/ask/composer-ui/getSubmissionPolicy.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useInputActions, useShouldTriggerFocus, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { useAskInputAutosuggestActions } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputAutosuggestActions.js';
import { useAskInputSubmitWithMentions } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSubmitWithMentions.js';
import { useAskInputSuggestionSubmission } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSuggestionSubmission.js';
import { useAskInputSuggestionView } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSuggestionView.js';
import { useBlankStateSuggest } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useBlankStateSuggest.js';
import { useSuggestDropdown } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useSuggestDropdown.js';
import { ASK_INPUT_MENU } from '#pplx/frontend/apps/ask/types/askInputMenu.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useCallback, useEffect, useMemo } from 'react';
export function useAskInputCore({ value, json, querySource, disableSubmission = false, disableInput = false, requireAttachment = false, autofocus = true, hasPendingAttachments = false, hasAdditionalContent = false, onChange, onFocus, onBlur, handleSubmit, handleUpdateAutosuggestions, autosuggestionsEnabled = true, errorMessage, sources, attachments, selectedSearchMode, isInputActive, setIsInputActive, updateOnFocus = false, inFlight = false, resultsLength = 0, quote, isMentionMenuOpened, isCometHome = false, reason, placement = 'bottom', onDisabledSubmit, onKeyDown, }) {
    const { activeMenu, inputRef } = useAskInputState();
    const shouldTriggerFocus = useShouldTriggerFocus();
    const { setShouldTriggerFocus } = useInputActions();
    const { blankStateSuggestions, shouldUnsetSuggestionsOnUnfocus } = useAskInputState();
    const { setSuggestions } = useAskInputActions();
    const { onActiveMenuChange } = useAskInputActions();
    const { device: { isAndroid }, } = useWebPlatform();
    const { compareModels } = useThreadConfig();
    const hasQuery = useMemo(() => hasAdditionalContent ||
        hasAskInputQuery({ quote, value, attachments, hasPendingAttachments }), [attachments, hasAdditionalContent, hasPendingAttachments, quote, value]);
    // Model Council requires at least 2 models selected
    const needsMoreModels = selectedSearchMode === SearchMode.AGENTIC_RESEARCH && compareModels.length < 2;
    const isDisabledWithoutQuery = getSubmissionPolicy({
        disableSubmission,
        disableInput,
        hasError: !!errorMessage,
        needsMoreModels,
        requireAttachment,
        hasAttachment: !!attachments?.length,
        hasPendingUploads: hasPendingAttachments,
        draftLength: value.length,
    });
    const isDisabled = isDisabledWithoutQuery || !hasQuery;
    const handleSubmitForSuggestion = useAskInputSubmitWithMentions({ json, handleSubmit });
    const fillInput = useCallback((query) => {
        onChange(query);
        inputRef.current?.focus();
    }, [onChange, inputRef]);
    const handleSuggestionSubmission = useAskInputSuggestionSubmission({
        handleSubmit: handleSubmitForSuggestion,
        inputQueryForAnalytics: value,
        fillInput,
        searchModeOverride: selectedSearchMode,
        querySource,
    });
    const handleSuggestionView = useAskInputSuggestionView({
        inputQuery: value,
        querySource,
    });
    const { focusedIndex, setFocusedIndex, suggestionsDisabled, userInputQuery, handleSuggestDropdownNavigation, } = useSuggestDropdown({
        autosuggestionsEnabled,
        inputQuery: value,
        inputJson: json,
        isInputActive: isInputActive && autosuggestionsEnabled,
        onChange,
        handleSuggestionView,
        handleSuggestionSubmission,
        querySource,
        hasThreadContent: inFlight || resultsLength > 0,
        isMentionMenuOpened,
        placement,
        inputRef,
    });
    useBlankStateSuggest({
        sources,
        attachments,
        selectedSearchMode,
        suggestionsDisabled,
        querySource,
        query: value,
        isCometHome,
        reason,
    });
    const focusInput = useCallback(() => {
        inputRef.current?.focus();
        setIsInputActive(true);
    }, [inputRef, setIsInputActive]);
    const handleFocus = useCallback((e) => {
        onFocus?.(e);
        setIsInputActive(true);
        if (activeMenu !== ASK_INPUT_MENU.COMPUTER_COMMAND_MENU) {
            onActiveMenuChange(null);
        }
        if (updateOnFocus) {
            handleUpdateAutosuggestions(getQueryWithoutSourceMentions(json?.root, inputRef.current?.value ?? ''));
        }
        if (isAndroid) {
            setTimeout(() => {
                inputRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 300);
        }
    }, [
        onFocus,
        activeMenu,
        onActiveMenuChange,
        handleUpdateAutosuggestions,
        setIsInputActive,
        updateOnFocus,
        inputRef,
        isAndroid,
        json,
    ]);
    useEffect(() => {
        if (shouldTriggerFocus && autofocus) {
            focusInput();
            setShouldTriggerFocus(false);
        }
    }, [shouldTriggerFocus, focusInput, setShouldTriggerFocus, autofocus]);
    const handleBlur = useCallback(() => {
        // returns whether we did unfocus the input
        onBlur?.();
        setIsInputActive(false);
        setFocusedIndex(-1);
        if (shouldUnsetSuggestionsOnUnfocus) {
            setSuggestions([]);
        }
        return true;
    }, [onBlur, setFocusedIndex, setIsInputActive, shouldUnsetSuggestionsOnUnfocus, setSuggestions]);
    const { updateSuggestions } = useAskInputAutosuggestActions({
        blankStateSuggestions,
        handleUpdateAutosuggestions,
        selectedSearchMode,
        setSuggestions,
        suggestionsDisabled,
    });
    const handleChange = useCallback((newInput, nextJson) => {
        onChange(newInput, nextJson);
        updateSuggestions(newInput, nextJson);
    }, [onChange, updateSuggestions]);
    const handleKeyDown = useAskInputKeydown({
        value,
        json,
        isDisabled,
        handleSubmit: handleSubmitForSuggestion,
        handleSuggestDropdownNavigation,
        querySource,
        onDisabledSubmit,
        onKeyDown,
    });
    useEffect(() => {
        // Blur the active input when a menu is opened
        if (activeMenu !== null) {
            if (activeMenu === ASK_INPUT_MENU.COMPUTER_COMMAND_MENU) {
                focusInput();
                return;
            }
            inputRef.current?.blur();
        }
        // When a menu is closed, focus the ask input
        else if (autofocus) {
            // Don't steal focus from keyboard tab navigation (WCAG 9.2.4.3 Focus Order).
            if (document.activeElement?.closest('[role="tablist"]')) {
                return;
            }
            focusInput();
        }
    }, [activeMenu, inputRef, focusInput, autofocus]);
    return useMemo(() => ({
        isDisabled,
        isDisabledWithoutQuery,
        focusedIndex,
        setFocusedIndex,
        userInputQuery,
        handleSuggestionSubmission,
        handleFocus,
        handleBlur,
        handleChange,
        handleKeyDown,
        focusInput,
        hasQuery,
    }), [
        focusInput,
        focusedIndex,
        handleBlur,
        handleChange,
        handleFocus,
        handleKeyDown,
        handleSuggestionSubmission,
        hasQuery,
        isDisabled,
        isDisabledWithoutQuery,
        setFocusedIndex,
        userInputQuery,
    ]);
}
//# sourceMappingURL=useAskInputCore.js.map